(() => {
  const GALLONS_PER_CUBIC_FOOT = 7.48052;
  const DEFAULT_LOCALE = document.documentElement.lang || "en-US";

  function boot() {
    document.querySelectorAll("[data-pool-heat-pump-calculator]").forEach(initializeCalculator);
  }

  function initializeCalculator(root) {
    if (root.dataset.poolCalculatorReady === "true") return;
    root.dataset.poolCalculatorReady = "true";

    const sectionId = root.dataset.sectionId || "";
    const configurations = window.DELLA_POOL_HEAT_PUMP_CONFIG || {};
    const config = configurations[sectionId] || {};
    const apiBaseUrl = String(config.apiBaseUrl || "").replace(/\/$/, "");
    const locale = String(config.locale || DEFAULT_LOCALE);
    const currency = String(config.currency || "USD");
    const monthNames = Array.from({ length: 12 }, (_, index) => (
      new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" })
        .format(new Date(Date.UTC(2024, index, 1)))
    ));
    const numberFormatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    const decimalFormatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    const currencyFormatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });
    const form = root.querySelector("[data-calculator-form]");
    if (!form) return;

    const elements = {
      form,
      lookupButton: root.querySelector('[data-action="lookup-context"]'),
      lookupLabel: root.querySelector("[data-lookup-label]"),
      calculateButton: root.querySelector("[data-calculate-button]"),
      calculateLabel: root.querySelector("[data-calculate-label]"),
      zip: root.querySelector('[data-field="zipCode"]'),
      locationSummary: root.querySelector("[data-location-summary]"),
      locationName: root.querySelector("[data-location-name]"),
      locationDetail: root.querySelector("[data-location-detail]"),
      contextStatus: root.querySelector("[data-context-status]"),
      contextWarnings: root.querySelector("[data-context-warnings]"),
      geometryPreview: root.querySelector("[data-geometry-preview]"),
      previewArea: root.querySelector("[data-preview-area]"),
      previewVolume: root.querySelector("[data-preview-volume]"),
      monthsSummary: root.querySelector("[data-months-summary]"),
      resultsCard: root.querySelector("[data-results-card]"),
      resultsEmpty: root.querySelector("[data-results-empty]"),
      resultsContent: root.querySelector("[data-results-content]"),
      calculationStatus: root.querySelector("[data-calculation-status]"),
      requiredCapacity: root.querySelector("[data-required-capacity]"),
      requiredCapacityKw: root.querySelector("[data-required-capacity-kw]"),
      designSummary: root.querySelector("[data-design-summary]"),
      resultArea: root.querySelector("[data-result-area]"),
      resultVolume: root.querySelector("[data-result-volume]"),
      resultStartingTemp: root.querySelector("[data-result-starting-temp]"),
      resultTargetTemp: root.querySelector("[data-result-target-temp]"),
      resultMonths: root.querySelector("[data-result-months]"),
      designMonth: root.querySelector("[data-design-month]"),
      coverSummary: root.querySelector("[data-cover-summary]"),
      modelCard: root.querySelector("[data-model-card]"),
      modelBadge: root.querySelector("[data-model-badge]"),
      modelStatus: root.querySelector("[data-model-status]"),
      modelName: root.querySelector("[data-model-name]"),
      modelDetail: root.querySelector("[data-model-detail]"),
      modelLink: root.querySelector("[data-model-link]"),
      modelCompare: root.querySelector("[data-model-compare]"),
      comparisonModelSku: root.querySelector('[data-field="comparisonModelSku"]'),
      comparisonPrevious: root.querySelector('[data-action="comparison-previous"]'),
      comparisonNext: root.querySelector('[data-action="comparison-next"]'),
      modelCompareState: root.querySelector("[data-model-compare-state]"),
      modelCompareName: root.querySelector("[data-model-compare-name]"),
      modelCompareNote: root.querySelector("[data-model-compare-note]"),
      recommendationPicks: root.querySelector("[data-recommendation-picks]"),
      recommendationPicksKicker: root.querySelector(".phc-picks-heading .phc-summary-label"),
      recommendationPicksTitle: root.querySelector(".phc-picks-heading h3"),
      lowestPricePick: root.querySelector('[data-pick-card="lowest-price"]'),
      bestOverallPick: root.querySelector('[data-pick-card="best-overall"]'),
      activeModelPrice: root.querySelector("[data-active-model-price]"),
      activeModelCapacity: root.querySelector("[data-active-model-capacity]"),
      heatUpSummary: root.querySelector("[data-heat-up-summary]"),
      heatUpTime: root.querySelector("[data-heat-up-time]"),
      heatUpDetail: root.querySelector("[data-heat-up-detail]"),
      costSummary: root.querySelector("[data-cost-summary]"),
      annualHeatPumpCost: root.querySelector("[data-annual-heat-pump-cost]"),
      annualGasCost: root.querySelector("[data-annual-gas-cost]"),
      annualSavings: root.querySelector("[data-annual-savings]"),
      multiYearSavings: root.querySelector("[data-multi-year-savings]"),
      costNote: root.querySelector("[data-cost-note]"),
      aiAdvice: root.querySelector("[data-ai-advice]"),
      aiAdviceStatus: root.querySelector("[data-ai-advice-status]"),
      aiAdviceContent: root.querySelector("[data-ai-advice-content]"),
      monthlyCaption: root.querySelector("[data-monthly-caption]"),
      monthlyTable: root.querySelector("[data-monthly-table]"),
      monthlyEmpty: root.querySelector("[data-monthly-empty]"),
      assumptionsList: root.querySelector("[data-assumptions-list]"),
      resultWarnings: root.querySelector("[data-result-warnings]"),
      wizardStage: root.querySelector("[data-wizard-stage]"),
      wizardPanels: Array.from(root.querySelectorAll("[data-wizard-step]")),
      progressItems: Array.from(root.querySelectorAll("[data-progress-step]")),
      currentStep: root.querySelector("[data-current-step]"),
      answerPool: root.querySelector("[data-answer-pool]"),
      answerLocation: root.querySelector("[data-answer-location]"),
      answerConditions: root.querySelector("[data-answer-conditions]"),
      answerResult: root.querySelector("[data-answer-result]"),
      startCalculator: root.querySelector('[data-action="start-calculator"]'),
      showHowItWorks: root.querySelector('[data-action="show-how-it-works"]'),
      howItWorks: root.querySelector("[data-how-it-works]"),
      capacityClass: root.querySelector("[data-capacity-class]"),
    };

    const state = {
      context: null,
      contextSequence: 0,
      calculateSequence: 0,
      contextLoading: false,
      calculationLoading: false,
      manualRates: {
        electricity: false,
        naturalGas: false,
      },
      rateMetadata: {
        electricity: null,
        naturalGas: null,
      },
      hasSentRateOverride: {
        electricity: false,
        naturalGas: false,
      },
      comparisonModels: [],
      automaticModelSku: "",
      zipLookupTimer: 0,
      currentWizardStep: 1,
    };

    function query(field) {
      return root.querySelector('[data-field="' + field + '"]');
    }

    function queryAll(field) {
      return Array.from(root.querySelectorAll('[data-field="' + field + '"]'));
    }

    function selectedValue(field) {
      const inputs = queryAll(field);
      const selected = inputs.find((input) => input.checked);
      return selected ? selected.value : "";
    }

    function numericValue(field) {
      const input = query(field);
      if (!input || String(input.value || "").trim() === "") return null;
      const parsed = Number(input && input.value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    function selectedMonths() {
      return queryAll("usageMonths")
        .filter((input) => input.checked)
        .map((input) => Number(input.value))
        .filter((value) => Number.isInteger(value) && value >= 1 && value <= 12)
        .sort((left, right) => left - right);
    }

    function monthName(value) {
      const number = Number(value);
      return Number.isInteger(number) && number >= 1 && number <= 12
        ? monthNames[number - 1]
        : typeof value === "string" && value
          ? value
          : "";
    }

    function formatCurrency(value) {
      return value === null ? "—" : currencyFormatter.format(value);
    }

    function apiUrl(path) {
      return apiBaseUrl + path;
    }

    function emit(event, details) {
      const payload = Object.assign({ event, calculator: "pool_heat_pump" }, details || {});
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
      root.dispatchEvent(new CustomEvent("della:pool-calculator", {
        bubbles: true,
        detail: payload,
      }));
    }

    function setText(element, value) {
      if (element) element.textContent = value;
    }

    function setHidden(element, hidden) {
      if (element) element.hidden = hidden;
    }

    function setStatus(message, tone) {
      if (!elements.contextStatus) return;
      elements.contextStatus.textContent = message;
      elements.contextStatus.dataset.tone = tone || "neutral";
    }

    function setCalculationStatus(message, tone) {
      if (!elements.calculationStatus) return;
      elements.calculationStatus.textContent = message;
      elements.calculationStatus.dataset.tone = tone || "neutral";
    }

    function setBusy(button, label, busy, busyText) {
      if (!button) return;
      button.disabled = busy;
      button.setAttribute("aria-busy", busy ? "true" : "false");
      if (label) label.textContent = busy ? busyText : button.dataset.defaultLabel;
    }

    function setFormBusy(busy) {
      form.querySelectorAll("input, select, textarea, button").forEach((control) => {
        if (busy) {
          control.dataset.poolCalculatorWasDisabled = control.disabled ? "true" : "false";
          control.disabled = true;
          return;
        }
        if (control.dataset.poolCalculatorWasDisabled === "false") control.disabled = false;
        delete control.dataset.poolCalculatorWasDisabled;
      });
    }

    function initializeButtonLabels() {
      if (elements.lookupButton) {
        elements.lookupButton.dataset.defaultLabel = elements.lookupLabel
          ? elements.lookupLabel.textContent.trim()
          : elements.lookupButton.textContent.trim();
      }
      if (elements.calculateButton) {
        elements.calculateButton.dataset.defaultLabel = elements.calculateLabel
          ? elements.calculateLabel.textContent.trim()
          : elements.calculateButton.textContent.trim();
      }
    }

    function setFieldError(field, message) {
      const error = root.querySelector('[data-field-error="' + field + '"]');
      const input = query(field);
      if (error) {
        error.textContent = message || "";
        error.hidden = !message;
      }
      if (input) {
        input.setAttribute("aria-invalid", message ? "true" : "false");
        const fieldShell = input.closest(".phc-field");
        if (fieldShell) fieldShell.classList.toggle("has-error", Boolean(message));
      }
    }

    function clearFieldErrors() {
      root.querySelectorAll("[data-field-error]").forEach((error) => {
        error.textContent = "";
        error.hidden = true;
      });
      root.querySelectorAll('[data-field]').forEach((input) => {
        input.setAttribute("aria-invalid", "false");
        const fieldShell = input.closest(".phc-field");
        if (fieldShell) fieldShell.classList.remove("has-error");
      });
    }

    function firstInputForError(errors) {
      return Object.keys(errors)
        .map((field) => query(field))
        .find(Boolean);
    }

    function validateZip(value) {
      return /^\d{5}$/.test(String(value || "").trim());
    }

    function boundedNumber(field, label, value, errors, minimum, maximum) {
      if (!Number.isFinite(value) || value < minimum || value > maximum) {
        errors[field] = "Enter a " + label + " from " + minimum + " to " + maximum + ".";
      }
    }

    function getGeometryInput() {
      const inputMode = selectedValue("poolInputMode") || "known-volume";
      const shape = selectedValue("poolShape") || "rectangle";
      if (inputMode === "known-volume") {
        return {
          inputMode,
          shape,
          volumeGallons: numericValue("volumeGallons"),
          averageDepthFt: numericValue("averageDepthKnown"),
        };
      }
      return {
        inputMode,
        shape,
        lengthFt: numericValue("lengthFt"),
        widthFt: numericValue("widthFt"),
        diameterFt: numericValue("diameterFt"),
        averageDepthFt: shape === "round"
          ? numericValue("averageDepthRound")
          : numericValue("averageDepthDimensions"),
      };
    }

    function calculateGeometry(geometry) {
      const depth = geometry.averageDepthFt;
      if (!Number.isFinite(depth) || depth <= 0) return null;
      if (geometry.inputMode === "known-volume") {
        const volume = geometry.volumeGallons;
        if (!Number.isFinite(volume) || volume <= 0) return null;
        return {
          surfaceAreaSqFt: volume / GALLONS_PER_CUBIC_FOOT / depth,
          volumeGallons: volume,
        };
      }
      if (geometry.shape === "round") {
        const diameter = geometry.diameterFt;
        if (!Number.isFinite(diameter) || diameter <= 0) return null;
        const surfaceAreaSqFt = Math.PI * Math.pow(diameter / 2, 2);
        return {
          surfaceAreaSqFt,
          volumeGallons: surfaceAreaSqFt * depth * GALLONS_PER_CUBIC_FOOT,
        };
      }
      if (!Number.isFinite(geometry.lengthFt) || !Number.isFinite(geometry.widthFt)
        || geometry.lengthFt <= 0 || geometry.widthFt <= 0) return null;
      const surfaceAreaSqFt = geometry.lengthFt * geometry.widthFt;
      return {
        surfaceAreaSqFt,
        volumeGallons: surfaceAreaSqFt * depth * GALLONS_PER_CUBIC_FOOT,
      };
    }

    function updateGeometryPreview() {
      const geometry = calculateGeometry(getGeometryInput());
      if (!geometry) {
        setText(elements.previewArea, "—");
        setText(elements.previewVolume, "—");
        return;
      }
      setText(elements.previewArea, numberFormatter.format(Math.round(geometry.surfaceAreaSqFt)) + " sq ft");
      setText(elements.previewVolume, numberFormatter.format(Math.round(geometry.volumeGallons)) + " gal");
    }

    function updateMonthsSummary() {
      const months = selectedMonths();
      if (!elements.monthsSummary) return;
      if (!months.length) {
        elements.monthsSummary.textContent = "Select at least one month.";
        return;
      }
      if (months.length === 12) {
        elements.monthsSummary.textContent = "All 12 months selected.";
        return;
      }
      elements.monthsSummary.textContent = months.map((month) => monthNames[month - 1].slice(0, 3)).join(", ") + " selected.";
    }

    function updatePoolMode() {
      const inputMode = selectedValue("poolInputMode") || "known-volume";
      root.querySelectorAll("[data-mode-panel]").forEach((panel) => {
        panel.hidden = panel.dataset.modePanel !== inputMode;
      });
      const shape = selectedValue("poolShape") || "rectangle";
      root.querySelectorAll("[data-shape-fields]").forEach((panel) => {
        panel.hidden = inputMode !== "dimensions" || panel.dataset.shapeFields !== shape;
      });
      updateGeometryPreview();
    }

    function updateAnswerSummaries() {
      const geometry = calculateGeometry(getGeometryInput());
      const poolType = selectedValue("poolType") === "in-ground" ? "In-ground" : "Above-ground";
      if (elements.answerPool) {
        elements.answerPool.textContent = geometry
          ? poolType + " · " + numberFormatter.format(Math.round(geometry.volumeGallons)) + " gal"
          : "Not answered";
      }
      if (elements.answerLocation) {
        const locationName = elements.locationName && !elements.locationSummary.hidden
          ? elements.locationName.textContent.trim()
          : "";
        const zip = String(elements.zip && elements.zip.value || "").trim();
        elements.answerLocation.textContent = locationName || (validateZip(zip) ? zip : "Not answered");
      }
      if (elements.answerConditions) {
        const target = numericValue("targetWaterTempF");
        const months = selectedMonths();
        const cover = selectedValue("coverMode") === "covered-when-not-in-use" ? "Covered" : "No cover";
        elements.answerConditions.textContent = Number.isFinite(target) && months.length
          ? target + "°F · " + months.length + " month" + (months.length === 1 ? "" : "s") + " · " + cover
          : "Not answered";
      }
    }

    function showWizardStep(step, options) {
      const nextStep = Math.max(1, Math.min(4, Number(step) || 1));
      state.currentWizardStep = nextStep;
      elements.wizardPanels.forEach((panel) => {
        const active = Number(panel.dataset.wizardStep) === nextStep;
        panel.hidden = !active;
        panel.classList.toggle("is-active", active);
      });
      elements.progressItems.forEach((item) => {
        const itemStep = Number(item.dataset.progressStep);
        item.classList.toggle("is-active", itemStep === nextStep);
        item.classList.toggle("is-complete", itemStep < nextStep);
      });
      setText(elements.currentStep, String(nextStep));
      updateAnswerSummaries();
      const settings = options || {};
      if (settings.scroll && elements.wizardStage) {
        elements.wizardStage.scrollIntoView({ behavior: settings.instant ? "auto" : "smooth", block: "start" });
      }
    }

    function formatRateMetadata(metadata, manual, kind) {
      if (manual) return "Manual rate from your utility bill or estimate.";
      if (!metadata) return "Enter a rate manually or update ZIP Code to load an estimate.";
      const source = metadata.source || metadata.sourceName || "Estimated local energy rate";
      const granularity = metadata.granularity
        ? String(metadata.granularity).replace(/-/g, " ")
        : "state average";
      const asOf = metadata.asOfDate || metadata.period || metadata.asOf;
      return source + " · " + granularity + (asOf ? " · as of " + asOf : "");
    }

    function updateRateMetadata() {
      const electricity = root.querySelector('[data-rate-meta="electricity"]');
      const naturalGas = root.querySelector('[data-rate-meta="naturalGas"]');
      setText(electricity, formatRateMetadata(
        state.rateMetadata.electricity,
        state.manualRates.electricity,
        "electricity",
      ));
      setText(naturalGas, formatRateMetadata(
        state.rateMetadata.naturalGas,
        state.manualRates.naturalGas,
        "natural gas",
      ));
    }

    function appendList(element, values) {
      if (!element) return;
      element.replaceChildren();
      const items = Array.isArray(values) ? values.filter(Boolean) : [];
      items.forEach((value) => {
        const item = document.createElement("li");
        item.textContent = String(value);
        element.append(item);
      });
      element.hidden = items.length === 0;
    }

    function extractWarnings(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value.map(String);
      if (Array.isArray(value.warnings)) return value.warnings.map(String);
      return [];
    }

    function setContextResult(context) {
      state.context = context || null;
      const location = context && context.location;
      const climate = context && context.climate;
      const rates = context && context.energyRates;
      const electricityRate = getNumber(firstDefined(
        rates && rates.electricityRatePerKwh,
        rates && rates.electricity && rates.electricity.ratePerKwh,
      ));
      const naturalGasRate = getNumber(firstDefined(
        rates && rates.naturalGasRatePerTherm,
        rates && rates.naturalGas && rates.naturalGas.ratePerTherm,
      ));

      if (location && elements.locationSummary) {
        const locationName = [location.city, location.state].filter(Boolean).join(", ");
        const detailParts = [
          location.zipCode || (elements.zip && elements.zip.value.trim()),
          location.timeZone,
          climate && (climate.source || climate.sourceName),
          climate && climate.asOfDate && "climate through " + climate.asOfDate,
        ].filter(Boolean);
        setText(elements.locationName, locationName || "Location found");
        setText(elements.locationDetail, detailParts.join(" · "));
        elements.locationSummary.hidden = false;
      }

      const electricityInput = query("electricityRatePerKwh");
      if (electricityInput && !state.manualRates.electricity && electricityRate !== null) {
        electricityInput.value = electricityRate.toFixed(4);
      }
      const gasInput = query("naturalGasRatePerTherm");
      if (gasInput && !state.manualRates.naturalGas && naturalGasRate !== null) {
        gasInput.value = naturalGasRate.toFixed(4);
      }

      state.rateMetadata.electricity = rates && rates.electricity
        ? rates.electricity
        : electricityRate !== null
          ? { source: "Estimated local electricity rate", granularity: "state" }
          : null;
      state.rateMetadata.naturalGas = rates && rates.naturalGas
        ? rates.naturalGas
        : naturalGasRate !== null
          ? { source: "Estimated local natural gas rate", granularity: "state" }
          : null;
      updateRateMetadata();

      const warnings = []
        .concat(extractWarnings(context && context.warnings))
        .concat(extractWarnings(rates && rates.electricity))
        .concat(extractWarnings(rates && rates.naturalGas));
      appendList(elements.contextWarnings, unique(warnings));

      const available = [
        climate && Array.isArray(climate.conditions) && climate.conditions.length ? "climate conditions" : null,
        electricityRate !== null ? "electricity rate" : null,
        naturalGasRate !== null ? "natural gas rate" : null,
      ].filter(Boolean);
      const unavailable = [
        !(climate && Array.isArray(climate.conditions) && climate.conditions.length) ? "climate data" : null,
        electricityRate === null ? "electricity rate" : null,
        naturalGasRate === null ? "natural gas rate" : null,
      ].filter(Boolean);
      if (unavailable.length) {
        setStatus(
          (available.length ? "Loaded " + available.join(", ") + ". " : "")
            + "Could not load " + unavailable.join(" or ") + ". You can enter energy rates manually; sizing will be retried when you calculate.",
          "warning",
        );
      } else {
        setStatus("Location, climate conditions, and energy-rate estimates are ready. Each rate's source and geographic scope are shown below.", "success");
      }
      updateAnswerSummaries();
    }

    async function lookupContext(options) {
      const settings = options || {};
      const zip = String(elements.zip && elements.zip.value || "").trim();
      setFieldError("zipCode", "");
      if (!validateZip(zip)) {
        setFieldError("zipCode", "Enter a valid five-digit U.S. ZIP Code.");
        if (!settings.silent) setStatus("Enter a valid five-digit U.S. ZIP Code before looking up local estimates.", "error");
        return false;
      }
      if (!apiBaseUrl) {
        setStatus("The calculator service is not configured. You can still enter rates manually.", "error");
        return false;
      }

      const sequence = ++state.contextSequence;
      state.contextLoading = true;
      setBusy(elements.lookupButton, elements.lookupLabel, true, "Looking up…");
      setStatus("Looking up location, multi-year climate conditions, and estimated energy rates…", "loading");
      try {
        const response = await fetch(apiUrl("/api/pool-heat-pump-context?zip=" + encodeURIComponent(zip)), {
          headers: { Accept: "application/json" },
          signal: AbortSignal.timeout(15000),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          const fieldErrors = payload.fieldErrors || payload.errors;
          if (fieldErrors && typeof fieldErrors === "object") {
            Object.keys(fieldErrors).forEach((field) => {
              const mappedField = field === "zip" ? "zipCode" : field;
              const detail = Array.isArray(fieldErrors[field])
                ? fieldErrors[field][0]
                : fieldErrors[field];
              setFieldError(mappedField, String(detail));
            });
          }
          throw new Error(payload.error || "ZIP lookup is unavailable.");
        }
        if (sequence !== state.contextSequence) return false;
        setContextResult(payload);
        emit("pool_zip_lookup_completed", {
          success: true,
          has_climate: Boolean(payload.climate && payload.climate.conditions && payload.climate.conditions.length),
          has_electricity_rate: getNumber(payload.energyRates && payload.energyRates.electricityRatePerKwh) !== null,
          has_natural_gas_rate: getNumber(payload.energyRates && payload.energyRates.naturalGasRatePerTherm) !== null,
        });
        return true;
      } catch (error) {
        if (sequence !== state.contextSequence) return false;
        const message = error && error.name === "TimeoutError"
          ? "ZIP lookup timed out. Please try again or enter energy rates manually."
          : (error && error.message) || "ZIP lookup is unavailable. Please try again or enter energy rates manually.";
        setStatus(message, "error");
        appendList(elements.contextWarnings, [message]);
        emit("pool_zip_lookup_completed", { success: false });
        return false;
      } finally {
        if (sequence === state.contextSequence) {
          state.contextLoading = false;
          setBusy(elements.lookupButton, elements.lookupLabel, false, "");
        }
      }
    }

    function collectInput(validate) {
      const geometry = getGeometryInput();
      const startingWaterTempF = numericValue("startingWaterTempF");
      const targetWaterTempF = numericValue("targetWaterTempF");
      const electricityRatePerKwh = numericValue("electricityRatePerKwh");
      const naturalGasRatePerTherm = numericValue("naturalGasRatePerTherm");
      const gasHeaterEfficiency = numericValue("gasHeaterEfficiency");
      const sizingMarginPct = numericValue("sizingMarginPct");
      const errors = {};

      if (!validateZip(elements.zip && elements.zip.value)) {
        errors.zipCode = "Enter a valid five-digit U.S. ZIP Code.";
      }
      if (geometry.inputMode === "known-volume") {
        boundedNumber("volumeGallons", "pool volume", geometry.volumeGallons, errors, 100, 1000000);
        boundedNumber("averageDepthKnown", "average depth", geometry.averageDepthFt, errors, 0.5, 30);
      } else if (geometry.shape === "round") {
        boundedNumber("diameterFt", "diameter", geometry.diameterFt, errors, 1, 500);
        boundedNumber("averageDepthRound", "average depth", geometry.averageDepthFt, errors, 0.5, 30);
      } else {
        boundedNumber("lengthFt", "length", geometry.lengthFt, errors, 1, 500);
        boundedNumber("widthFt", "width", geometry.widthFt, errors, 1, 500);
        boundedNumber("averageDepthDimensions", "average depth", geometry.averageDepthFt, errors, 0.5, 30);
      }
      if (!calculateGeometry(geometry)) errors.pool = "Enter valid pool dimensions or a valid known volume.";
      if (!Number.isFinite(startingWaterTempF) || startingWaterTempF < 32 || startingWaterTempF > 95) {
        errors.startingWaterTempF = "Choose a starting water temperature between 32°F and 95°F.";
      }
      if (!Number.isFinite(targetWaterTempF) || targetWaterTempF < 70 || targetWaterTempF > 95) {
        errors.targetWaterTempF = "Choose a target water temperature between 70°F and 95°F.";
      }
      const usageMonths = selectedMonths();
      if (!usageMonths.length) errors.usageMonths = "Select at least one month of use.";
      if (electricityRatePerKwh !== null && electricityRatePerKwh < 0) {
        errors.electricityRatePerKwh = "Electricity rate cannot be negative.";
      }
      if (naturalGasRatePerTherm !== null && naturalGasRatePerTherm < 0) {
        errors.naturalGasRatePerTherm = "Natural gas rate cannot be negative.";
      }
      if (!Number.isFinite(gasHeaterEfficiency) || gasHeaterEfficiency < 70 || gasHeaterEfficiency > 98) {
        errors.gasHeaterEfficiency = "Enter a gas heater efficiency from 70% to 98%.";
      }
      if (!Number.isFinite(sizingMarginPct) || sizingMarginPct < 0 || sizingMarginPct > 30) {
        errors.sizingMarginPct = "Enter a sizing margin from 0% to 30%.";
      }

      if (validate) {
        clearFieldErrors();
        Object.keys(errors).forEach((field) => setFieldError(field, errors[field]));
      }

      return {
        errors,
        payload: {
          zipCode: String(elements.zip && elements.zip.value || "").trim(),
          pool: {
            inputMode: geometry.inputMode,
            shape: geometry.shape,
            lengthFt: optionalNumber(geometry.lengthFt),
            widthFt: optionalNumber(geometry.widthFt),
            diameterFt: optionalNumber(geometry.diameterFt),
            averageDepthFt: optionalNumber(geometry.averageDepthFt),
            volumeGallons: optionalNumber(geometry.volumeGallons),
          },
          startingWaterTempF: optionalNumber(startingWaterTempF),
          targetWaterTempF: optionalNumber(targetWaterTempF),
          usageMonths,
          coverMode: selectedValue("coverMode") || "none",
          electricityRatePerKwh: optionalNumber(electricityRatePerKwh),
          naturalGasRatePerTherm: optionalNumber(naturalGasRatePerTherm),
          gasHeaterEfficiency: optionalNumber(gasHeaterEfficiency),
          sizingMarginPct: optionalNumber(sizingMarginPct),
          comparisonModelSku: String(query("comparisonModelSku") && query("comparisonModelSku").value || "").trim() || undefined,
          rateOverrides: {
            electricity: state.manualRates.electricity,
            naturalGas: state.manualRates.naturalGas,
          },
        },
      };
    }

    function validateWizardStep(step) {
      const input = collectInput(false);
      const geometry = getGeometryInput();
      const fieldsByStep = {
        1: geometry.inputMode === "known-volume"
          ? ["volumeGallons", "averageDepthKnown", "pool"]
          : geometry.shape === "round"
            ? ["diameterFt", "averageDepthRound", "pool"]
            : ["lengthFt", "widthFt", "averageDepthDimensions", "pool"],
        2: ["zipCode", "electricityRatePerKwh", "naturalGasRatePerTherm", "gasHeaterEfficiency", "sizingMarginPct"],
        3: ["startingWaterTempF", "targetWaterTempF", "usageMonths", "coverMode"],
      };
      const allowed = fieldsByStep[step] || [];
      const errors = Object.keys(input.errors)
        .filter((field) => allowed.includes(field))
        .reduce((result, field) => {
          result[field] = input.errors[field];
          return result;
        }, {});
      allowed.forEach((field) => setFieldError(field, ""));
      Object.keys(errors).forEach((field) => setFieldError(field, errors[field]));
      const firstInvalid = firstInputForError(errors);
      if (firstInvalid) firstInvalid.focus();
      return Object.keys(errors).length === 0;
    }

    async function calculate() {
      const input = collectInput(true);
      if (Object.keys(input.errors).length) {
        setCalculationStatus("Check the highlighted fields", "error");
        const errorFields = Object.keys(input.errors);
        const poolFields = ["volumeGallons", "averageDepthKnown", "diameterFt", "averageDepthRound", "lengthFt", "widthFt", "averageDepthDimensions", "pool"];
        const locationFields = ["zipCode", "electricityRatePerKwh", "naturalGasRatePerTherm", "gasHeaterEfficiency", "sizingMarginPct"];
        showWizardStep(
          errorFields.some((field) => poolFields.includes(field))
            ? 1
            : errorFields.some((field) => locationFields.includes(field))
              ? 2
              : 3,
          { scroll: true },
        );
        const firstInvalid = firstInputForError(input.errors);
        if (firstInvalid) firstInvalid.focus();
        return;
      }
      if (!apiBaseUrl) {
        setCalculationStatus("The calculator service is not configured.", "error");
        return;
      }

      const sequence = ++state.calculateSequence;
      state.calculationLoading = true;
      showWizardStep(4, { scroll: true });
      setFormBusy(true);
      setBusy(elements.calculateButton, elements.calculateLabel, true, "Calculating…");
      setCalculationStatus("Calculating your selected-month estimate…", "loading");
      if (elements.resultsContent) elements.resultsContent.hidden = true;
      if (elements.resultsEmpty) elements.resultsEmpty.hidden = false;
      if (elements.resultsCard) elements.resultsCard.dataset.state = "loading";
      try {
        const response = await fetch(apiUrl("/api/pool-heat-pump-calculate"), {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "text/plain;charset=UTF-8",
          },
          body: JSON.stringify(input.payload),
          signal: AbortSignal.timeout(20000),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          const message = payload.error || payload.message || "Calculation could not be completed.";
          const fieldErrors = payload.fieldErrors || payload.errors;
          if (fieldErrors && typeof fieldErrors === "object") {
            clearFieldErrors();
            Object.keys(fieldErrors).forEach((field) => {
              const detail = Array.isArray(fieldErrors[field])
                ? fieldErrors[field][0]
                : fieldErrors[field];
              setFieldError(field, String(detail));
            });
          }
          throw new Error(message);
        }
        if (sequence !== state.calculateSequence) return;
        const estimate = payload.estimate || payload;
        renderEstimate(estimate);
        emit("pool_calculation_completed", {
          has_cost_comparison: Boolean(estimate.annual || estimate.annualCosts),
          has_recommended_model: Boolean(estimate.recommendedModel),
        });
        if (estimate.recommendedModel) emit("pool_model_recommended", { available: true });
      } catch (error) {
        if (sequence !== state.calculateSequence) return;
        const message = error && error.name === "TimeoutError"
          ? "Calculation timed out. Please try again."
          : (error && error.message) || "Calculation could not be completed.";
        setCalculationStatus(message, "error");
        if (elements.resultsEmpty) {
          elements.resultsEmpty.hidden = false;
          const copy = elements.resultsEmpty.querySelector("p");
          if (copy) copy.textContent = message;
        }
        if (elements.resultsContent) elements.resultsContent.hidden = true;
        if (elements.resultsCard) elements.resultsCard.dataset.state = "error";
        emit("pool_calculation_failed", { reason: "api_or_validation_error" });
      } finally {
        if (sequence === state.calculateSequence) {
          state.calculationLoading = false;
          setFormBusy(false);
          setBusy(elements.calculateButton, elements.calculateLabel, false, "");
        }
      }
    }

    function renderEstimate(estimate) {
      const geometry = estimate.geometry || estimate.poolGeometry || {};
      const requiredCapacityBtuH = getNumber(firstDefined(
        estimate.requiredCapacityBtuH,
        estimate.requiredHeatingCapacityBtuH,
      ));
      const requiredCapacityKw = getNumber(firstDefined(
        estimate.requiredCapacityKw,
        estimate.requiredHeatingCapacityKw,
      ));
      const designMonthValue = estimate.designMonth || (estimate.designClimate && estimate.designClimate.month);
      const designMonth = monthName(designMonthValue);
      const startingWaterTemp = getNumber(estimate.startingWaterTempF);
      const targetWaterTemp = getNumber(estimate.targetWaterTempF);
      const designClimate = estimate.designClimate || {};
      const sizingMargin = getNumber(estimate.sizingMarginPct);
      const designAir = getNumber(firstDefined(
        designClimate.designAirTempF,
        estimate.designAirTempF,
        estimate.designAmbientTempF,
      ));
      const model = estimate.recommendedModel || estimate.model || null;
      const operatingRange = getOperatingRange(firstDefined(
        estimate.operatingRange,
        model && model.operatingRange,
      ));
      const annual = estimate.annual || estimate.annualCosts || {};
      const multiYear = estimate.multiYear || estimate.multiYearSavings || {};
      const warnings = unique(extractWarnings(estimate.warnings)
        .concat(extractWarnings(estimate.modelSelection))
        .concat(operatingRange && operatingRange.status !== "NO_DEFROST_CAUTION"
          && typeof operatingRange.rationale === "string"
          ? [operatingRange.rationale]
          : []));

      setText(elements.requiredCapacity, requiredCapacityBtuH === null
        ? "Capacity unavailable"
        : numberFormatter.format(Math.round(requiredCapacityBtuH)) + " BTU/h");
      setText(elements.requiredCapacityKw, requiredCapacityKw === null
        ? ""
        : decimalFormatter.format(requiredCapacityKw) + " kW thermal");
      setText(elements.designSummary, designMonth
        ? "Sizing uses " + designMonth + (designAir === null ? "" : " at " + decimalFormatter.format(designAir) + "°F design air temperature")
          + (sizingMargin === null ? "" : " with a " + decimalFormatter.format(sizingMargin) + "% safety margin") + "."
        : "Sizing uses the highest-load selected month.");

      const surfaceArea = getNumber(firstDefined(geometry.surfaceAreaSqFt, geometry.surfaceArea));
      const volume = getNumber(firstDefined(geometry.volumeGallons, geometry.volume));
      setText(elements.resultArea, surfaceArea === null ? "—" : numberFormatter.format(Math.round(surfaceArea)) + " sq ft");
      setText(elements.resultVolume, volume === null ? "—" : numberFormatter.format(Math.round(volume)) + " gal");
      setText(elements.resultStartingTemp, startingWaterTemp === null ? "—" : decimalFormatter.format(startingWaterTemp) + "°F");
      setText(elements.resultTargetTemp, targetWaterTemp === null ? "—" : decimalFormatter.format(targetWaterTemp) + "°F");
      const selectedMonths = Array.isArray(estimate.usageMonths)
        ? estimate.usageMonths.map(monthName).filter(Boolean)
        : (Array.isArray(estimate.monthly) ? estimate.monthly.map((row) => monthName(row.month)).filter(Boolean) : []);
      setText(elements.resultMonths, selectedMonths.length ? selectedMonths.join(", ") : "—");
      setText(elements.designMonth, designMonth || "—");
      setText(elements.coverSummary, estimate.coverMode === "covered-when-not-in-use"
        ? "Covered"
        : "No cover");
      const recommendedCapacity = getNumber(firstDefined(
        model && model.ratedCapacityBtuH,
        model && model.capacityBtuH,
        requiredCapacityBtuH,
      ));
      setText(elements.capacityClass, recommendedCapacity === null
        ? "A suitable DELLA pool heat pump is the best fit."
        : "A " + numberFormatter.format(Math.round(recommendedCapacity / 1000) * 1000)
          + " BTU-class pool heat pump is the best fit.");
      setText(elements.answerResult, model && (model.name || model.sku)
        ? model.name || model.sku
        : "Capacity estimate ready");

      renderModel(model, estimate, requiredCapacityBtuH);
      renderRecommendationPicks(estimate);
      renderModelComparison(estimate);
      renderInitialHeatUp(estimate.initialHeatUp);
      renderCosts(annual, multiYear, estimate);
      renderAiAdvice(estimate.aiAdvice);
      renderMonthlyRows(estimate.monthly || estimate.monthlyBreakdown || []);
      renderAssumptions(estimate.assumptions || [], warnings);

      if (elements.resultsEmpty) elements.resultsEmpty.hidden = true;
      if (elements.resultsContent) elements.resultsContent.hidden = false;
      setCalculationStatus(model ? "Estimate ready" : "Capacity estimate ready", model ? "success" : "warning");
      if (elements.resultsCard) elements.resultsCard.dataset.state = model ? "ready" : "capacity-only";
      updateAnswerSummaries();
    }

    function renderAiAdvice(advice) {
      if (!elements.aiAdvice) return;
      const content = advice && typeof advice.content === "string" ? advice.content.trim() : "";
      const available = advice && advice.status === "available" && Boolean(content);
      elements.aiAdvice.hidden = !available;
      if (!available) {
        setText(elements.aiAdviceStatus, "");
        setText(elements.aiAdviceContent, "");
        return;
      }
      setText(elements.aiAdviceStatus, "Personalized summary");
      setText(elements.aiAdviceContent, content);
    }

    function renderRecommendationPicks(estimate) {
      if (!elements.recommendationPicks) return;
      const models = Array.isArray(estimate.comparisonModels)
        ? estimate.comparisonModels.filter((item) => item && item.sku && item.eligible === true)
        : [];
      const highlights = estimate.recommendationHighlights || {};
      const activeSku = String(estimate.comparisonModelSku
        || (estimate.recommendedModel && estimate.recommendedModel.sku)
        || "");
      const renderPick = (card, role, sku) => {
        if (!card || !sku) {
          if (card) card.hidden = true;
          return false;
        }
        const model = models.find((item) => item && item.sku === sku);
        if (!model) {
          card.hidden = true;
          return false;
        }
        card.hidden = false;
        card.dataset.sku = model.sku;
        card.setAttribute("aria-pressed", model.sku === activeSku ? "true" : "false");
        setText(root.querySelector('[data-pick-name="' + role + '"]'), model.name || model.sku);
        const price = getNumber(model.price);
        setText(root.querySelector('[data-pick-price="' + role + '"]'),
          price === null ? "See product price" : formatCurrency(price));
        const hours = getNumber(model.initialHeatUp && model.initialHeatUp.theoreticalMinimumHours);
        setText(root.querySelector('[data-pick-time="' + role + '"]'),
          hours === null ? "Heat-up unavailable" : decimalFormatter.format(hours) + " hr Boost");
        const savings = getNumber(model.savings);
        setText(root.querySelector('[data-pick-savings="' + role + '"]'),
          savings === null
            ? "Savings unavailable"
            : savings >= 0
              ? "Save " + formatCurrency(savings) + " vs gas"
              : formatCurrency(Math.abs(savings)) + " more than gas");
        return true;
      };
      const hasLowestPrice = renderPick(
        elements.lowestPricePick,
        "lowest-price",
        highlights.lowestPriceSku,
      );
      const hasBestOverall = renderPick(
        elements.bestOverallPick,
        "best-overall",
        highlights.bestOverallSku && highlights.bestOverallSku !== highlights.lowestPriceSku
          ? highlights.bestOverallSku
          : "",
      );
      const hasTwoChoices = hasLowestPrice && hasBestOverall;
      setText(elements.recommendationPicksKicker, hasTwoChoices ? "Two good choices" : "Recommended match");
      setText(elements.recommendationPicksTitle, hasTwoChoices ? "Choose what matters most" : "One model fits this scenario");
      elements.recommendationPicks.hidden = !hasLowestPrice && !hasBestOverall;
    }

    function renderModelComparison(estimate) {
      if (!elements.modelCompare || !elements.comparisonModelSku) return;
      const models = Array.isArray(estimate.comparisonModels)
        ? estimate.comparisonModels.filter((item) => item && item.sku && item.eligible === true)
        : [];
      if (!models.length) {
        state.comparisonModels = [];
        elements.modelCompare.hidden = true;
        return;
      }
      state.comparisonModels = models;
      const activeSku = String(estimate.comparisonModelSku || "").trim();
      const automatic = estimate.automaticRecommendedModel || null;
      state.automaticModelSku = String(automatic && automatic.sku || "");
      const selected = activeSku
        ? state.comparisonModels.find((item) => item.sku === activeSku) || null
        : automatic;
      elements.comparisonModelSku.value = selected && selected.sku === activeSku ? activeSku : "";
      elements.modelCompare.hidden = false;
      setText(elements.modelCompareState, activeSku ? "Comparing" : "Automatic recommendation");
      const selectedCapacity = getNumber(selected && selected.ratedCapacityBtuH);
      setText(elements.modelCompareName,
        selected && (selected.name || selected.sku)
          ? (selected.name || selected.sku) + (selectedCapacity === null
            ? ""
            : " · " + numberFormatter.format(Math.round(selectedCapacity)) + " BTU/h")
          : "No automatic model available",
      );
      const hasChoices = state.comparisonModels.length > 1;
      if (elements.comparisonPrevious) elements.comparisonPrevious.disabled = !hasChoices;
      if (elements.comparisonNext) elements.comparisonNext.disabled = !hasChoices;
      if (activeSku) {
        const automaticName = automatic && (automatic.name || automatic.sku);
        setText(elements.modelCompareNote,
          "Showing this model's first heat-up time and heat-pump operating cost."
            + (automaticName ? " Automatic recommendation: " + automaticName + "." : ""),
        );
      } else {
        setText(elements.modelCompareNote,
          "Choose a model to compare its first heat-up time and heat-pump operating cost. Pool heat loss and natural-gas cost stay the same.",
        );
      }
    }

    function moveComparisonModel(direction) {
      if (!elements.comparisonModelSku || !state.comparisonModels.length) return;
      const choices = state.comparisonModels.map((model) => model.sku);
      const current = String(elements.comparisonModelSku.value || state.automaticModelSku || choices[0]);
      const currentIndex = Math.max(0, choices.indexOf(current));
      const nextIndex = (currentIndex + direction + choices.length) % choices.length;
      elements.comparisonModelSku.value = choices[nextIndex];
      calculate();
    }

    function renderModel(model, estimate, requiredCapacityBtuH) {
      const status = estimate.modelSelectionStatus || estimate.catalogStatus || "";
      const operatingRange = getOperatingRange(firstDefined(
        estimate.operatingRange,
        model && model.operatingRange,
      ));
      const operatingStatus = operatingRange && operatingRange.status || "";
      if (!model) {
        const unsupportedAutomaticRecommendation = operatingStatus === "UNSUPPORTED_AUTOMATIC_RECOMMENDATION";
        setText(elements.modelBadge, "Product match");
        setText(elements.modelStatus, unsupportedAutomaticRecommendation
          ? "Automatic match unavailable"
          : status === "MISSING_CATALOG"
          ? "Catalog unavailable"
          : status === "DESIGN_CAPACITY_UNVERIFIED"
            ? "Design capacity unverified"
            : "No match");
        setText(elements.modelName, unsupportedAutomaticRecommendation
          ? "Automatic recommendation unavailable at this design temperature"
          : status === "MISSING_CATALOG" || status === "DESIGN_CAPACITY_UNVERIFIED"
          ? "Required capacity only"
          : "No available model meets the selected design condition");
        const gap = getNumber(firstDefined(estimate.capacityGapBtuH, estimate.modelCapacityGapBtuH));
        const detail = unsupportedAutomaticRecommendation && typeof operatingRange.rationale === "string"
          ? operatingRange.rationale
          : status === "MISSING_CATALOG"
          ? "No verified pool heat-pump catalog is available, so this estimate does not display a model."
          : status === "DESIGN_CAPACITY_UNVERIFIED"
            ? "Published model ratings do not include capacity at this design air and water condition, so no model is recommended."
          : gap !== null && gap > 0
            ? "The closest catalog capacity is short by " + numberFormatter.format(Math.round(gap)) + " BTU/h. Contact sales or an installer."
            : "Review the selected design condition with sales or a qualified installer.";
        setText(elements.modelDetail, detail);
        setText(elements.activeModelPrice, "—");
        setText(elements.activeModelCapacity, "—");
        if (elements.modelLink) elements.modelLink.hidden = true;
        if (elements.modelCard) elements.modelCard.dataset.state = "unavailable";
        return;
      }

      const name = model.name || model.title || model.sku || "Recommended heat pump";
      const manualComparison = status === "MANUAL_COMPARISON"
        || model.selectionBasis === "MANUAL_COMPARISON";
      const preliminary = manualComparison
        || status === "PRELIMINARY_RECOMMENDED"
        || model.selectionBasis === "PRELIMINARY_PUBLISHED_ENVELOPE"
        || operatingStatus === "PRELIMINARY_INSTALLER_REVIEW";
      const defrostCaution = operatingStatus === "DEFROST_CAUTION";
      const hasPublishedDesignCapacity = model.capacityAtDesignUsesPublishedPerformance === true;
      const capacity = getNumber(firstDefined(
        hasPublishedDesignCapacity ? model.capacityAtDesignBtuH : undefined,
        model.ratedCapacityBtuH,
        model.capacityBtuH,
      ));
      const price = getNumber(model.price);
      setText(elements.activeModelPrice, price === null ? "See price" : formatCurrency(price));
      setText(elements.activeModelCapacity, capacity === null
        ? "Capacity unavailable"
        : numberFormatter.format(Math.round(capacity)) + " BTU/h");
      const hasFactoryPerformancePoints = Array.isArray(model.factoryPerformancePoints)
        && model.factoryPerformancePoints.length > 0;
      const cop = getNumber(firstDefined(model.effectiveCop, model.ratedCop, model.cop));
      const pointsUsed = Boolean(model.performancePointsUsed || model.usesPerformanceData || hasFactoryPerformancePoints);
      const detailParts = [];
      if (capacity !== null) {
        detailParts.push(numberFormatter.format(Math.round(capacity)) + " BTU/h "
          + (hasPublishedDesignCapacity ? "at published design condition" : "published rated capacity"));
      }
      const publishedVolumeLimit = getNumber(model.publishedPoolVolumeLimitGallons);
      if (preliminary && publishedVolumeLimit !== null) {
        detailParts.push("published pool guidance up to "
          + numberFormatter.format(Math.round(publishedVolumeLimit)) + " gal "
          + (estimate.coverMode === "covered-when-not-in-use" ? "with a cover" : "without a cover"));
      }
      if (cop !== null) {
        detailParts.push("COP " + decimalFormatter.format(cop)
          + (hasFactoryPerformancePoints
            ? " from factory performance data"
            : pointsUsed
              ? " from performance data"
              : " estimated from published COP points"));
      }
      const automaticRecommendationBoundary = getNumber(
        operatingRange && operatingRange.minimumAutomaticRecommendationAirTempF,
      );
      const minimumAir = automaticRecommendationBoundary === null
        ? getNumber(model.minimumOperatingAirTempF)
        : null;
      const maximumWater = getNumber(model.maximumWaterTempF);
      if (automaticRecommendationBoundary !== null) {
        detailParts.push("automatic-recommendation boundary "
          + decimalFormatter.format(automaticRecommendationBoundary) + "°F");
      }
      if (minimumAir !== null) detailParts.push("minimum ambient " + decimalFormatter.format(minimumAir) + "°F");
      if (maximumWater !== null) detailParts.push("maximum pool water " + decimalFormatter.format(maximumWater) + "°F");
      if (hasFactoryPerformancePoints) {
        detailParts.push("Cost estimates use the factory 63% RH / 80°F water curve across 50–80°F ambient");
        detailParts.push("Below 50°F, machine capacity and COP provisionally use the 50°F factory point while pool heat loss still uses local temperature");
        detailParts.push("80°F ambient / 80% RH is a separate favorable reference point");
        const targetWaterTemp = getNumber(estimate.targetWaterTempF);
        if (targetWaterTemp !== null && targetWaterTemp !== 80) {
          const formattedTargetWaterTemp = Number.isInteger(targetWaterTemp)
            ? numberFormatter.format(targetWaterTemp)
            : decimalFormatter.format(targetWaterTemp);
          detailParts.push("Your " + formattedTargetWaterTemp
            + "°F target is a planning estimate adjusted from the factory 80°F water curve");
        }
      } else if (Array.isArray(model.performancePoints) && model.performancePoints.length) {
        const points = model.performancePoints
          .map((point) => {
            const pointCop = getNumber(point && point.cop);
            const pointAir = getNumber(point && point.airTempF);
            return pointCop === null || pointAir === null
              ? ""
              : "COP " + decimalFormatter.format(pointCop) + " @ " + decimalFormatter.format(pointAir) + "°F ambient";
          })
          .filter(Boolean);
        if (points.length) detailParts.push("published test points: " + points.join(", "));
      }
      if (model.sku) detailParts.push("SKU " + model.sku);
      if (requiredCapacityBtuH !== null && capacity !== null && capacity < requiredCapacityBtuH) {
        detailParts.push("Does not meet required capacity");
      }
      if (operatingRange && typeof operatingRange.rationale === "string") {
        detailParts.push(operatingRange.rationale);
      }
      const highlights = estimate.recommendationHighlights || {};
      setText(elements.modelBadge,
        model.sku === highlights.lowestPriceSku
          ? "Lowest price match"
          : model.sku === highlights.bestOverallSku
            ? "Best overall"
            : "Selected model");
      setText(elements.modelStatus, preliminary
        ? "Installer review"
        : defrostCaution
          ? "Defrost caution"
          : "");
      setText(elements.modelName, name);
      setText(elements.modelDetail, detailParts.join(" · ") || (preliminary
        ? "Catalog candidate based on current published product guidance."
        : "Catalog match based on the current selected design condition."));
      if (elements.modelLink) {
        const url = model.productUrl || model.url || model.onlineStoreUrl;
        elements.modelLink.hidden = !url;
        if (url) elements.modelLink.href = url;
      }
      if (elements.modelCard) elements.modelCard.dataset.state = preliminary ? "preliminary" : "recommended";
    }

    function renderInitialHeatUp(initialHeatUp) {
      if (!elements.heatUpSummary) return;
      if (!initialHeatUp || typeof initialHeatUp !== "object") {
        elements.heatUpSummary.hidden = true;
        return;
      }
      elements.heatUpSummary.hidden = false;
      const result = initialHeatUp;
      const status = String(result.status || "MISSING_MODEL");
      const hours = getNumber(result.theoreticalMinimumHours);
      const starting = getNumber(result.startingWaterTempF);
      const target = getNumber(result.targetWaterTempF);
      const ambient = getNumber(result.ambientAirTempF);
      const normalCapacity = getNumber(result.normalCapacityBtuH);
      const boostCapacity = getNumber(result.boostCapacityBtuH);
      const multiplier = getNumber(result.boostMultiplier);
      const capacityReferenceAir = getNumber(result.capacityReferenceAirTempF);
      const minimumFactoryAirTempFloorApplied = result.minimumFactoryAirTempFloorApplied === true;

      if (status === "READY" && hours !== null) {
        setText(elements.heatUpTime, decimalFormatter.format(hours) + " hours minimum");
        const condition = starting === null || target === null
          ? "the entered water-temperature rise"
          : decimalFormatter.format(starting) + "°F → " + decimalFormatter.format(target) + "°F";
        const capacityDetail = normalCapacity === null || boostCapacity === null
          ? ""
          : " Normal output " + numberFormatter.format(Math.round(normalCapacity))
            + " BTU/h; modeled Boost output " + numberFormatter.format(Math.round(boostCapacity)) + " BTU/h.";
        const factoryFloorDetail = minimumFactoryAirTempFloorApplied
          ? " Because factory data below 50°F is unavailable, machine output uses the "
            + decimalFormatter.format(capacityReferenceAir === null ? 50 : capacityReferenceAir)
            + "°F factory point; actual colder-weather output may be lower and heat-up slower."
          : "";
        setText(elements.heatUpDetail,
          "For " + condition
          + (ambient === null ? "" : " at " + decimalFormatter.format(ambient) + "°F design-month average air")
          + "." + capacityDetail
          + factoryFloorDetail
          + " Uses the provisional factory-reported " + (multiplier === null ? "1.5" : decimalFormatter.format(multiplier))
          + "× multiplier on the normal 63% RH / 80°F-water capacity curve. This theoretical minimum excludes simultaneous pool heat loss and defrost; Boost is not used for COP or operating cost."
        );
        elements.heatUpSummary.dataset.available = "true";
        return;
      }

      if (status === "NO_HEATING_REQUIRED") {
        setText(elements.heatUpTime, "No temperature rise needed");
        setText(elements.heatUpDetail, "Starting water temperature is already at or above the selected target.");
      } else if (status === "OUTSIDE_FACTORY_CURVE") {
        setText(elements.heatUpTime, "Heat-up time unavailable");
        setText(elements.heatUpDetail,
          "The design-month average air temperature"
          + (ambient === null ? "" : " of " + decimalFormatter.format(ambient) + "°F")
          + " is outside the 50–80°F factory capacity curve, so no Boost capacity is extrapolated."
        );
      } else if (status === "MISSING_FACTORY_CAPACITY") {
        setText(elements.heatUpTime, "Heat-up time unavailable");
        setText(elements.heatUpDetail, "The matched model does not have both normal-condition 50°F and 80°F factory capacity points.");
      } else {
        setText(elements.heatUpTime, "Heat-up time unavailable");
        setText(elements.heatUpDetail, "A matched model with a complete factory capacity curve is required for the provisional Boost estimate.");
      }
      elements.heatUpSummary.dataset.available = "false";
    }

    function renderCosts(annual, multiYear, estimate) {
      const heatPumpCost = getNumber(firstDefined(
        annual.heatPumpCost,
        annual.heatPumpOperatingCost,
        estimate.annualHeatPumpCost,
      ));
      const gasCost = getNumber(firstDefined(
        annual.gasCost,
        annual.naturalGasCost,
        annual.gasOperatingCost,
        estimate.annualGasCost,
      ));
      const savings = getNumber(firstDefined(annual.savings, estimate.annualSavings));
      const savingsPct = getNumber(firstDefined(
        annual.savingsPct,
        annual.savingsPercent,
        estimate.annualSavingsPct,
      ));
      const fiveYear = getNumber(firstDefined(multiYear.fiveYearSavings, estimate.fiveYearSavings));
      const tenYear = getNumber(firstDefined(multiYear.tenYearSavings, estimate.tenYearSavings));

      setText(elements.annualHeatPumpCost, formatCurrency(heatPumpCost));
      setText(elements.annualGasCost, formatCurrency(gasCost));
      setText(elements.annualSavings, savings === null
        ? "—"
        : savings >= 0
          ? formatCurrency(savings)
          : "Additional " + formatCurrency(Math.abs(savings)));
      if (elements.annualSavings && savings !== null) {
        elements.annualSavings.closest("div").dataset.negative = savings < 0 ? "true" : "false";
      }
      const horizons = [];
      if (savingsPct !== null) horizons.push(decimalFormatter.format(savingsPct) + "% of selected-month gas cost");
      if (fiveYear !== null) horizons.push("5 years, same selected months: " + formatCurrency(fiveYear));
      if (tenYear !== null) horizons.push("10 years, same selected months: " + formatCurrency(tenYear));
      setText(elements.multiYearSavings, horizons.length
        ? horizons.join(" · ")
        : "requires both energy rates");

      const rateWarning = estimate.costComparisonStatus === "MISSING_RATES"
        || heatPumpCost === null || gasCost === null;
      const missingPerformance = estimate.costComparisonStatus === "MISSING_MODEL_PERFORMANCE";
      setText(elements.costNote, missingPerformance
        ? "A heat-pump cost comparison needs a matched model with published COP data. Natural-gas cost can still be shown when its rate is available."
        : rateWarning
        ? "Provide both electricity and natural gas rates to compare operating costs. Capacity sizing remains available."
        : (savings !== null && savings < 0
          ? "Estimated additional selected-month operating cost reflects the same delivered heat at the selected energy rates."
          : "Future selected-month savings assume electricity and natural gas prices remain unchanged."));
      if (elements.costSummary) elements.costSummary.dataset.available = rateWarning || missingPerformance ? "false" : "true";
    }

    function renderMonthlyRows(rows) {
      if (!elements.monthlyTable) return;
      elements.monthlyTable.replaceChildren();
      const normalizedRows = Array.isArray(rows) ? rows : [];
      normalizedRows.forEach((row) => {
        const tableRow = document.createElement("tr");
        const month = monthName(row.month || row.monthNumber || row.monthIndex) || "—";
        const designAir = getNumber(firstDefined(row.designAirTempF, row.designTemperatureF));
        const load = getNumber(firstDefined(row.monthlyThermalDemandBtu, row.thermalDemandBtu));
        const effectiveCop = getNumber(row.effectiveCop);
        const heatPumpKwh = getNumber(row.heatPumpElectricityKwh);
        const heatPumpCost = getNumber(firstDefined(row.heatPumpCost, row.heatPumpOperatingCost));
        const gasTherms = getNumber(row.gasTherms);
        const gasCost = getNumber(firstDefined(
          row.gasCost,
          row.naturalGasCost,
          row.gasOperatingCost,
        ));
        const savings = getNumber(row.savings);
        [
          month,
          designAir === null ? "—" : decimalFormatter.format(designAir) + "°F",
          load === null ? "—" : numberFormatter.format(Math.round(load)) + " BTU",
          effectiveCop === null ? "—" : decimalFormatter.format(effectiveCop),
          heatPumpKwh === null ? "—" : numberFormatter.format(Math.round(heatPumpKwh)) + " kWh",
          formatCurrency(heatPumpCost),
          gasTherms === null ? "—" : decimalFormatter.format(gasTherms) + " therms",
          formatCurrency(gasCost),
          savings === null ? "—" : (savings < 0 ? "Additional " : "") + formatCurrency(Math.abs(savings)),
        ].forEach((value) => {
          const cell = document.createElement("td");
          cell.textContent = value;
          tableRow.append(cell);
        });
        if (savings !== null && savings < 0) tableRow.dataset.negative = "true";
        elements.monthlyTable.append(tableRow);
      });
      setHidden(elements.monthlyEmpty, normalizedRows.length > 0);
      setText(elements.monthlyCaption, normalizedRows.length
        ? normalizedRows.length + " selected month" + (normalizedRows.length === 1 ? "" : "s")
        : "");
    }

    function renderAssumptions(assumptions, warnings) {
      const normalized = Array.isArray(assumptions)
        ? assumptions.map((value) => typeof value === "string" ? value : value && (value.label || value.description)).filter(Boolean)
        : [];
      appendList(elements.assumptionsList, normalized);
      appendList(elements.resultWarnings, warnings);
    }

    function scheduleZipLookup() {
      window.clearTimeout(state.zipLookupTimer);
      const zip = String(elements.zip && elements.zip.value || "").trim();
      if (!validateZip(zip)) return;
      state.zipLookupTimer = window.setTimeout(() => lookupContext({ silent: true }), 350);
    }

    function markManualRate(kind) {
      const field = kind === "electricity" ? "electricityRatePerKwh" : "naturalGasRatePerTherm";
      const input = query(field);
      const hasManualValue = getNumber(input && input.value) !== null;
      const wasManual = state.manualRates[kind];
      state.manualRates[kind] = hasManualValue;
      if (!hasManualValue && state.context) setContextResult(state.context);
      setFieldError(field, "");
      updateRateMetadata();
      if (hasManualValue && !wasManual && !state.hasSentRateOverride[kind]) {
        state.hasSentRateOverride[kind] = true;
        emit("pool_energy_rate_overridden", { energy_type: kind });
      }
    }

    initializeButtonLabels();
    updatePoolMode();
    updateMonthsSummary();
    updateGeometryPreview();
    updateRateMetadata();
    showWizardStep(1);
    updateAnswerSummaries();
    emit("pool_calculator_viewed");

    queryAll("poolInputMode").forEach((input) => input.addEventListener("change", () => {
      updatePoolMode();
      updateAnswerSummaries();
    }));
    queryAll("poolShape").forEach((input) => input.addEventListener("change", () => {
      updatePoolMode();
      updateAnswerSummaries();
    }));
    queryAll("poolType").forEach((input) => input.addEventListener("change", updateAnswerSummaries));
    queryAll("coverMode").forEach((input) => input.addEventListener("change", updateAnswerSummaries));
    ["lengthFt", "widthFt", "diameterFt", "averageDepthDimensions", "averageDepthRound", "volumeGallons", "averageDepthKnown"]
      .forEach((field) => {
        const input = query(field);
        if (input) input.addEventListener("input", () => {
          setFieldError(field, "");
          setFieldError("pool", "");
          updateGeometryPreview();
          updateAnswerSummaries();
        });
      });
    queryAll("usageMonths").forEach((input) => input.addEventListener("change", () => {
      setFieldError("usageMonths", "");
      updateMonthsSummary();
      updateAnswerSummaries();
    }));
    if (elements.comparisonPrevious) {
      elements.comparisonPrevious.addEventListener("click", () => moveComparisonModel(-1));
    }
    if (elements.comparisonNext) {
      elements.comparisonNext.addEventListener("click", () => moveComparisonModel(1));
    }
    [elements.lowestPricePick, elements.bestOverallPick].forEach((card) => {
      if (!card) return;
      card.addEventListener("click", () => {
        const sku = String(card.dataset.sku || "");
        if (!sku || !elements.comparisonModelSku) return;
        elements.comparisonModelSku.value = sku;
        calculate();
      });
    });
    ["startingWaterTempF", "targetWaterTempF", "gasHeaterEfficiency", "sizingMarginPct"].forEach((field) => {
      const input = query(field);
      if (input) input.addEventListener("input", () => {
        setFieldError(field, "");
        updateAnswerSummaries();
      });
    });
    const electricityInput = query("electricityRatePerKwh");
    if (electricityInput) electricityInput.addEventListener("input", () => markManualRate("electricity"));
    const gasInput = query("naturalGasRatePerTherm");
    if (gasInput) gasInput.addEventListener("input", () => markManualRate("naturalGas"));
    if (elements.zip) {
      elements.zip.addEventListener("input", () => {
        setFieldError("zipCode", "");
        scheduleZipLookup();
        updateAnswerSummaries();
      });
      elements.zip.addEventListener("blur", () => {
        if (validateZip(elements.zip.value)) lookupContext({ silent: true });
      });
      elements.zip.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          lookupContext();
        }
      });
    }
    if (elements.lookupButton) elements.lookupButton.addEventListener("click", () => lookupContext());
    root.querySelectorAll('[data-action="wizard-next"]').forEach((button) => {
      button.addEventListener("click", async () => {
        const step = state.currentWizardStep;
        if (!validateWizardStep(step)) return;
        if (step === 2 && !state.context) await lookupContext({ silent: true });
        showWizardStep(step + 1, { scroll: true });
      });
    });
    root.querySelectorAll('[data-action="wizard-previous"]').forEach((button) => {
      button.addEventListener("click", () => showWizardStep(state.currentWizardStep - 1, { scroll: true }));
    });
    root.querySelectorAll('[data-action="wizard-restart"]').forEach((button) => {
      button.addEventListener("click", () => showWizardStep(1, { scroll: true }));
    });
    if (elements.startCalculator) {
      elements.startCalculator.addEventListener("click", () => showWizardStep(1, { scroll: true }));
    }
    if (elements.showHowItWorks && elements.howItWorks) {
      elements.showHowItWorks.addEventListener("click", () => {
        elements.howItWorks.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      calculate();
    });

    if (elements.zip && validateZip(elements.zip.value)) lookupContext({ silent: true });
  }

    function getNumber(value) {
      if (value === null || value === undefined || value === "") return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    function getOperatingRange(value) {
      return value && typeof value === "object" && typeof value.status === "string"
        ? value
        : null;
    }

    function firstDefined(...values) {
    return values.find((value) => value !== null && value !== undefined && value !== "");
  }

  function optionalNumber(value) {
    return Number.isFinite(value) ? value : undefined;
  }

  function unique(values) {
    return Array.from(new Set(values.filter(Boolean)));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
